import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS, TYPOGRAPHY } from '../../../../constants/interaxColors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { SprintMemberInitials } from './SprintMemberInitials'
import { SprintCompletionBadge } from './SprintCompletionBadge'
import { User } from '../../../../../../pure-js/types/User'
import { Team } from '../../../../../../pure-js/types/Team'

export interface SprintProgressHeaderProps {
  sprintTitle: string
  facilitatorData?: User
  team?: Team
  memberInitials: string[]
  remainingMemberCount: number
  completionPercentage: number
  className?: string
}

/**
 * SprintProgressHeader - Header section of sprint progress accordion
 * Displays sprint name, facilitator, team, members, and completion percentage
 */
export const SprintProgressHeader: React.FC<SprintProgressHeaderProps> = ({
  sprintTitle,
  facilitatorData,
  team,
  memberInitials,
  remainingMemberCount,
  completionPercentage,
  className = ''
}) => {
  const { t } = useTranslation()

  return (
    <Box className={className} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Title */}
      <Box
        component="h3"
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          mb: 0.1
        }}
      >
        {t('gamer.sprintProgress.yourSprintActivities', 'Your sprint activities')}
      </Box>

      {/* Content Row */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {/* Left Side - Sprint Name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 auto' }
          }}
        >
          <Box
            component="span"
            sx={{
              ...TYPOGRAPHY.headline5,
              color: INTERAX_COLORS.white
            }}
          >
            {sprintTitle}
          </Box>
        </Box>

        {/* Right Side - Meta Information */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 1 auto' }
          }}
        >
          {/* Facilitator */}
          {facilitatorData && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Box
                component="span"
                sx={{
                  ...TYPOGRAPHY.body3,
                  color: INTERAX_COLORS.thunderGrey
                }}
              >
                {t('gamer.sprintProgress.facilitator', 'Facilitator:')}
              </Box>
              <Box
                component="span"
                sx={{
                  ...TYPOGRAPHY.body3,
                  fontWeight: 600,
                  color: INTERAX_COLORS.white
                }}
              >
                {(facilitatorData as any).displayName || facilitatorData.email}
              </Box>
            </Box>
          )}

          {/* Team */}
          {team && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Box
                component="span"
                sx={{
                  ...TYPOGRAPHY.body3,
                  color: INTERAX_COLORS.thunderGrey
                }}
              >
                {t('gamer.sprintProgress.team', 'Team:')}
              </Box>
              <Box
                component="span"
                sx={{
                  ...TYPOGRAPHY.body3,
                  fontWeight: 600,
                  color: INTERAX_COLORS.white
                }}
              >
                {team.name}
              </Box>
            </Box>
          )}

          {/* Members */}
          <SprintMemberInitials initials={memberInitials} remainingCount={remainingMemberCount} />

          {/* Completion */}
          <SprintCompletionBadge completionPercentage={completionPercentage} />
        </Box>
      </Box>
    </Box>
  )
}
